import { useId, useState } from 'react';
import { cn } from '../../lib/cn';
import { useInViewOnce } from '../../hooks/useInViewOnce';
import { Icon } from './Icon';
import styles from './Accordion.module.css';

/**
 * Numbered accordion, matching the comp's FAQ (01-09 + chevron, one open).
 *
 * The open/close transition is CSS grid-template-rows (see the module CSS), so
 * this needs no animation library and no height measurement. Panels stay in the
 * DOM and are hidden with `inert` + `aria-hidden` when closed, which keeps their
 * content out of the tab order without unmounting it.
 */
export function Accordion({ items, defaultOpen = 0, className }) {
  const [open, setOpen] = useState(defaultOpen);
  const baseId = useId();
  const [ref, inView] = useInViewOnce();

  return (
    <div ref={ref} className={cn(styles.list, inView && styles.listIn, className)}>
      {items.map((item, i) => {
        const isOpen = open === i;
        const panelId = `${baseId}-panel-${i}`;
        const triggerId = `${baseId}-trigger-${i}`;

        return (
          <div key={item.q} className={styles.item}>
            <button
              type="button"
              id={triggerId}
              className={styles.trigger}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpen(isOpen ? -1 : i)}
            >
              <span className={styles.n}>{String(i + 1).padStart(2, '0')}</span>
              <span className={styles.q}>{item.q}</span>
              <Icon name="chevron-down" size={24} className={styles.chevron} />
            </button>

            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              className={cn(styles.panel, isOpen && styles.panelOpen)}
              // `inert` (React 19) takes the collapsed panel out of the tab
              // order and the accessibility tree without unmounting it, so the
              // grid-row transition still has something to animate.
              inert={!isOpen ? '' : undefined}
            >
              <div className={styles.panelInner}>
                <p className={cn(styles.answer, 'u-body-lg')}>{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
